/* eslint-disable import/order */
import { Box, DrawerContent, Text, useDisclosure } from '@chakra-ui/react';
import FullCalendar, { DatesSetArg, EventClickArg } from '@fullcalendar/react';
import debounce from 'lodash/debounce';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import adaptivePlugin from '@fullcalendar/adaptive';

import clsx from 'clsx';
import { format } from 'date-fns';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';

import { ControllableDrawer } from 'components/Drawer';
import { roles } from 'global/rolesAndPermissions';
import { getUserStore, hexToRgba } from 'utils/helpers';

import EventContent from './EventContent';
import ResourceLabelContent from './ResourceLabelContent';
import './Calendar.scss';
import {
  ILeaveRequestCalendarData,
  useFetchLeaveRequestCalendarData,
} from 'hooks/timeOff/useFetchLeaveRequestCalendarData';
import FilterGroup from 'containers/TimeOff/shared/FilterGroup';
import { useTranslation } from 'react-i18next';
import RequestDetails from 'containers/TimeOff/RightDrawer/RequestDetails';
import { IBulkDeleteRequest } from 'hooks/timeOff/useBulkDeleteRequests';
import RequestApproveConfirmationModal from 'containers/TimeOff/Modal/RequestApprovalConfirmationModal';
import RequestRejectConfirmationModal from 'containers/TimeOff/Modal/RequestRejectConfirmationModal';
import { initialRequestData } from 'containers/TimeOff/RightDrawer/RequestTimeOff/schema';
import RequestTimeOff from 'containers/TimeOff/RightDrawer/RequestTimeOff';

const Calendar: React.FC<unknown> = () => {
  const { t } = useTranslation(['timeOff', 'common']);

  const userStore = getUserStore();
  const firstDay = useMemo(() => userStore?.companyInfo?.start_week ?? 0, [userStore]);
  const fullCalendarRef = useRef<any>(null);

  const [dialogIds, setDialogIds] = useState<IBulkDeleteRequest>({ ids: [] });
  const [requestDetails, setRequestDetails] = useState<any>({});
  const [requestFormData, setRequestFormData] = useState<typeof initialRequestData>(initialRequestData);
  const [calendarData, setCalendarData] = useState({ events: [], resources: [] });
  const [searchQuery, setSearchQuery] = useState<ILeaveRequestCalendarData>({
    date_range: '',
    search: '',
    user_ids: '',
    category_ids: '',
  });

  const {
    isOpen: isRequestFormDrawerOpen,
    onOpen: onRequestFormDrawerOpen,
    onClose: onRequestFormDrawerClose,
  } = useDisclosure();
  const {
    isOpen: isRequestDetailDrawerOpen,
    onOpen: onRequestDetailDrawerOpen,
    onClose: onRequestDetailDrawerClose,
  } = useDisclosure();

  const {
    isOpen: isApprovalConfirmationModalOpen,
    onOpen: onApprovalConfirmationModalOpen,
    onClose: onApprovalConfirmationModalClose,
  } = useDisclosure();
  const {
    isOpen: isRejectConfirmationModalOpen,
    onOpen: onRejectConfirmationModalOpen,
    onClose: onRejectConfirmationModalClose,
  } = useDisclosure();

  const { data, isFetching, refetch } = useFetchLeaveRequestCalendarData(searchQuery, {
    enabled: searchQuery.date_range ? true : false,
  });

  useEffect(() => {
    if (data?.payload) {
      const events = Array.isArray(data?.payload?.requests)
        ? data?.payload?.requests.map((item: any) => {
            const baseColor =
              item?.category_color ||
              getComputedStyle(document.documentElement).getPropertyValue('--timeero-colors-primary-900').trim();

            const bgColor = hexToRgba(baseColor, 0.08);
            const textColor = baseColor;

            return {
              ...item,
              title: item?.full_name,
              startDate: item?.start,
              endDate: item?.end,
              resourceId: item?.user_id,
              request_duration: item?.duration,
              backgroundColor: bgColor,
              borderColor: textColor,
              textColor: textColor,
              display: 'block',
            };
          })
        : [];

      const fetchedResources = Array.isArray(data?.payload?.users)
        ? data?.payload?.users.map((item: any) => {
            return {
              ...item,
              user: item?.firstName + ' ' + item?.lastName,
              id: item?.userId,
            };
          })
        : [];

      setCalendarData({
        events: events,
        resources: fetchedResources,
      });
    }
  }, [data]);

  const handleDatesSet = useCallback((arg: DatesSetArg) => {
    const dateRange = format(arg.start, 'yyyy-MM-dd') + ',' + format(arg.end, 'yyyy-MM-dd');
    setSearchQuery((searchQueryParam: any) => ({ ...searchQueryParam, date_range: dateRange }));
  }, []);

  const handleDateClick = useCallback((arg: DateClickArg) => {
    if (userStore?.userInfo?.roleId !== roles.ADMIN && userStore?.permissions?.manageSchedules === false) return;

    const formData = {
      ...initialRequestData,
      start_date: arg.date,
      end_date: arg.date,
      start_time: format(arg.date, 'HH:mm:ss'),
      end_time: format(arg.date, 'HH:mm:ss'),
      user_ids: arg.resource?.extendedProps ? [arg.resource?.extendedProps] : [],
    };

    setRequestFormData(formData);
    onRequestFormDrawerOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEventClick = useCallback((arg: EventClickArg) => {
    setRequestDetails({ ...arg.event.extendedProps, id: arg.event.id });
    onRequestDetailDrawerOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEmployeeFilterFormSubmit = useCallback((selectedEmployees: any) => {
    const updatedEmployee = selectedEmployees.length ? selectedEmployees.map((item: any) => item.userId).join(',') : '';

    setSearchQuery((searchQueryParam: any) => ({
      ...searchQueryParam,
      user_ids: updatedEmployee,
    }));
  }, []);

  const handleTimeOffStatusChange = useCallback((status: ILabelValueOptions) => {
    setSearchQuery((searchQueryParam: any) => {
      let requestStatus = {};

      if (status?.value === 'Approved') {
        requestStatus = { is_approve: '1', is_reject: '0' };
      } else if (status?.value === 'Rejected') {
        requestStatus = { is_approve: '0', is_reject: '1' };
      } else if (status?.value === 'Pending') {
        requestStatus = { is_approve: '0', is_reject: '0' };
      } else {
        requestStatus = { is_approve: null, is_reject: undefined };
      }

      return { ...searchQueryParam, ...requestStatus };
    });
  }, []);

  const handleCategoryChange = useCallback((values: any) => {
    const selectedCategories = values.length ? values.map((item: any) => item.id).join(',') : values?.id || '';

    setSearchQuery((searchQueryParam: any) => ({
      ...searchQueryParam,
      category_ids: selectedCategories,
    }));
  }, []);

  const handleSearchInputChange = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value.toLowerCase().trim();

      setSearchQuery((searchQueryParam: any) => ({
        ...searchQueryParam,
        search: searchValue,
      }));
    }, 400),
    []
  );

  const handleSingleApprove = (id: number) => {
    setDialogIds({ ids: [id] });
    onApprovalConfirmationModalOpen();
  };

  const handleSingleReject = (id: number) => {
    setDialogIds({ ids: [id] });
    onRejectConfirmationModalOpen();
  };

  const handleRequestApprovalSuccess = () => {
    refetch();
    onApprovalConfirmationModalClose();
    onRequestDetailDrawerClose();
    setDialogIds({ ids: [] });
  };

  const handleRequestRejectSuccess = () => {
    refetch();
    onRequestDetailDrawerClose();
    onRejectConfirmationModalClose();
    setDialogIds({ ids: [] });
  };

  return (
    <Box>
      <ControllableDrawer isOpen={isRequestFormDrawerOpen} onClose={onRequestFormDrawerClose}>
        <DrawerContent>
          <RequestTimeOff onClose={onRequestFormDrawerClose} formData={requestFormData} />
        </DrawerContent>
      </ControllableDrawer>

      <ControllableDrawer isOpen={isRequestDetailDrawerOpen} onClose={onRequestDetailDrawerClose} size="lg">
        <RequestDetails
          onClose={onRequestDetailDrawerClose}
          requestDetails={requestDetails}
          handleSingleApprove={handleSingleApprove}
          handleSingleReject={handleSingleReject}
        />
      </ControllableDrawer>

      <RequestApproveConfirmationModal
        isModelOpen={isApprovalConfirmationModalOpen}
        onModalClose={onApprovalConfirmationModalClose}
        dialogIds={dialogIds}
        handleApproveSuccess={handleRequestApprovalSuccess}
      />

      <RequestRejectConfirmationModal
        dialogIds={dialogIds}
        isModelOpen={isRejectConfirmationModalOpen}
        onModalClose={onRejectConfirmationModalClose}
        handleRejectSuccess={handleRequestRejectSuccess}
      />

      <FilterGroup
        handleTimeOffStatusChange={handleTimeOffStatusChange}
        handleCategoryChange={handleCategoryChange}
        handleSearchInputChange={handleSearchInputChange}
        searchQuery={searchQuery}
        handleEmployeeFilterFormSubmit={handleEmployeeFilterFormSubmit}
        searchPlaceholder={
          t('timeOff:searchRequests') + (userStore?.userInfo?.roleId === 1 ? t('timeOff:orUsers') : '')
        }
      />

      <Box className={clsx('calendar-box', 'visible-print', isFetching && 'calendar-box--loading')} mt={4}>
        <FullCalendar
          ref={fullCalendarRef}
          schedulerLicenseKey={import.meta.env.VITE_FULL_CALENDAR_KEY}
          plugins={[resourceTimelinePlugin, dayGridPlugin, timeGridPlugin, interactionPlugin, adaptivePlugin]}
          initialView="resourceTimelineWeek"
          aspectRatio={1.5}
          height={'80vh'}
          firstDay={firstDay}
          resourceAreaWidth={'250px'}
          resourceAreaColumns={[
            {
              field: 'user',
              headerContent: (
                <Text fontWeight="semibold">
                  User's name <br />{' '}
                  <Text as="span" fontSize="xs" color="gray.500">
                    User's group(s)
                  </Text>
                </Text>
              ),
            },
          ]}
          displayEventEnd={true}
          editable={false}
          eventDurationEditable={false}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          datesSet={handleDatesSet}
          headerToolbar={{
            start: 'timelineWeek,gridMonth',
            center: 'title',
          }}
          views={{
            resourceTimelineWeek: {
              duration: { weeks: 1 },
              slotDuration: { days: 1 },
              slotLabelFormat: {
                weekday: 'short',
                day: '2-digit',
              },
            },
          }}
          customButtons={{
            gridMonth: {
              text: 'Month',
              click: () => {
                if (fullCalendarRef.current) {
                  fullCalendarRef.current.getApi().changeView('dayGridMonth');
                }
              },
            },
            timelineWeek: {
              text: 'Week',
              click: () => {
                if (fullCalendarRef.current) {
                  fullCalendarRef.current.getApi().changeView('resourceTimelineWeek');
                }
              },
            },
          }}
          resourceOrder="user"
          handleWindowResize={true}
          // showNonCurrentDates={false}
          resources={calendarData.resources}
          events={calendarData.events}
          resourceLabelContent={(arg: any) => <ResourceLabelContent arg={arg} />}
          eventContent={arg => <EventContent arg={arg} />}
        />
      </Box>
    </Box>
  );
};

export default Calendar;
